import { Link as RouterLink } from 'react-router';

import { Button } from '@/ui/button-2.tsx';

import Breadcrumbs from '../../components/Breadcrumbs/index.ts';
import Page from '../../components/Page/index.ts';
import RrsHandler from '../../components/RrsHandler/index.ts';
import Section from '../../components/Section/index.ts';
import useBrainSession from '../../services/BrainSession/useBrainSession.ts';
import { LANG } from '../../services/Config.js';
import Disclaimer from '../Common/Disclaimer.tsx';

import { RRS_INTEREST_RATE } from './RRS.ts';

const linkClassName =
  'typography-v1-legacy-p3 font-normal text-[#18A0FB]! no-underline hover:underline';

const chatTriggerButtonClassName = `${linkClassName} inline bg-transparent border-0 p-0 cursor-pointer text-left font-inherit`;

const articleRowClassName = 'mx-auto flex w-full max-w-[900px]! flex-col flex-wrap sm:flex-row';

const interestTableWrapClassName =
  'relative mx-auto mb-12 w-full max-w-[900px]! min-w-0 basis-full self-stretch overflow-x-auto rounded-lg border border-[#CEE2FF] shadow-[0px_2px_4px_rgba(0,0,0,0.02)] touch-pan-x';

const debtCheckButtonClassName = 'max-w-[550px] self-center sm:min-w-[330px]';

type Row = {
  lender: string;
  establishmentFee: string;
  termFee: string;
  nominalInterestRate: string;
  effectiveInterestRate: string;
  monthCost: string;
  totalCost: string;
  amount: string;
  repaymentTime: string;
};

const tableColumns: {
  id: keyof Row;
  label: string;
  align: 'left' | 'right';
  minWidth: number;
}[] = [
  {
    id: 'lender',
    label: 'Långiver',
    align: 'left',
    minWidth: 100,
  },
  {
    id: 'amount',
    label: 'Beløp',
    align: 'right',
    minWidth: 140,
  },
  {
    id: 'repaymentTime',
    label: 'Nedbetalingstid',
    minWidth: 150,
    align: 'right',
  },
  {
    id: 'establishmentFee',
    label: 'Etableringsgebyr',
    minWidth: 150,
    align: 'right',
  },
  {
    id: 'termFee',
    label: 'Termingebyr',
    minWidth: 120,
    align: 'right',
  },
  {
    id: 'nominalInterestRate',
    label: 'Nominell rente',
    minWidth: 140,
    align: 'right',
  },
  {
    id: 'effectiveInterestRate',
    label: 'Effektiv rente',
    minWidth: 130,
    align: 'right',
  },
  {
    id: 'monthCost',
    label: 'Måndelig kostnad',
    minWidth: 170,
    align: 'right',
  },
  {
    id: 'totalCost',
    label: 'Totalkostnad',
    minWidth: 150,
    align: 'right',
  },
];

function createData(
  lender: string,
  establishmentFee: string,
  termFee: string,
  nominalInterestRate: string,
  effectiveInterestRate: string,
  monthCost: string,
  totalCost: string,
): Row {
  const amount = '150 00000 kr';
  const repaymentTime = '5 år';
  return {
    lender,
    amount,
    repaymentTime,
    establishmentFee,
    termFee,
    nominalInterestRate,
    effectiveInterestRate,
    monthCost,
    totalCost,
  };
}

const banks = [
  createData('Bank1', '950,00 kr', '70,00 kr', '7,9%', '9,5%', '3 124 00 kr', '187 440,00 kr'),
  createData('Bank2', '497,00 kr', '29,00 kr', '8,4%', '9,9%', '3 142 00 kr', '189 017,00 kr'),
  createData('Bank3', '890,00 kr', '45,00 kr', '8,4%', '9,6%', '3 133 00 kr', '187 980,00 kr'),
  createData('Bank4', '900,00 kr', '40,00 kr', '10,7%', '12,3%', '3 290 00 kr', '198 300,00 kr'),
  createData('Bank5', '950,00 kr', '40,00 kr', '13,9%', '15,7%', '3 545 00 kr', '213 650,00 kr'),
];

const InterestComparisonTable = () => (
  <div className={interestTableWrapClassName}>
    <table className="w-full min-w-[920px] border-collapse border-hidden">
      <thead>
        <tr className="align-top">
          {tableColumns.map((column) => {
            const isFirstColumn = column.id === 'lender';
            return (
              <th
                key={column.id}
                scope="col"
                style={{ minWidth: column.minWidth }}
                className={
                  isFirstColumn
                    ? 'sticky left-0 z-10 w-[100px] min-w-[100px] border border-[#CEE2FF] bg-white px-4! py-2! text-left align-top typography-v1-legacy-table-cell font-normal text-v1-background-200'
                    : 'border border-[#CEE2FF] px-4! py-2! text-right align-top typography-v1-legacy-table-cell font-normal text-[#18A0FB]'
                }
              >
                {isFirstColumn ? (
                  <span
                    className="absolute top-[-8px] left-[100px] h-[253px] w-px bg-[#CEE2FF]"
                    aria-hidden
                  />
                ) : null}
                {column.label}
              </th>
            );
          })}
        </tr>
      </thead>
      <tbody>
        {banks.map((row) => (
          <tr key={row.lender} tabIndex={-1} className="align-top">
            {tableColumns.map((column) => {
              const value = row[column.id];
              const isFirstColumn = column.id === 'lender';
              const sharedCellStyle = { minWidth: column.minWidth };
              if (isFirstColumn) {
                return (
                  <th
                    key={column.label}
                    scope="row"
                    style={sharedCellStyle}
                    className="sticky left-0 z-10 w-[100px] min-w-[100px] border border-[#CEE2FF] bg-white px-4! py-2! text-left align-top typography-v1-legacy-table-cell font-normal text-v1-background-200"
                  >
                    {value}
                  </th>
                );
              }
              return (
                <td
                  key={column.label}
                  style={sharedCellStyle}
                  className="border border-[#CEE2FF] px-4! py-2! text-right align-top typography-v1-legacy-table-cell font-normal text-[#18A0FB]"
                >
                  {value}
                </td>
              );
            })}
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

const InterestRate: React.FC = () => {
  const { appStrings, routes } = LANG;
  const { toggleChat } = useBrainSession();

  const handleDebtCheckClick = () => {
    toggleChat({
      goal: 'debt_check',
      step: 'refin_present_terms_auto_new',
    });
  };

  return (
    <Page title={appStrings.MENU.INTEREST_RATE}>
      <RrsHandler {...RRS_INTEREST_RATE} />
      <Section>
        <div className="flex max-w-[900px]! flex-col [&>button]:self-center">
          <Breadcrumbs
            className="mb-8"
            items={[
              { text: appStrings.MENU.HOME, url: routes.HOME },
              {
                text: appStrings.MENU.MY_ECONOMY,
                url: routes.MY_ECONOMY,
              },
              { text: appStrings.MENU.INTEREST_RATE },
            ]}
          />
          <h1 className="mb-6 typography-v1-legacy-h2 text-[#18A0FB] sm:mb-8">
            Rente – hva er egentlig det?
          </h1>
          <h2 className="mb-4 typography-v1-legacy-subtitle1Bold">
            Når vi skal ta opp et lån er det for de fleste av oss én ting som er viktig, nemlig
            renten. Men hva er egentlig rente? Hva betyr nominell og effektiv rente, hva er
            forskjellen på dem og ikke minst – hvilken av disse to rentene bør avgjøre hvilket lån
            du velger?
          </h2>
          <p className="mb-8 typography-v1-legacy-subtitle2 sm:mb-14">
            Har du begynt å søke etter lån? Om du er på jakt etter det beste forbrukslånet,
            boliglånet, billånet eller båtlånet ser du garantert etter hvor du får den laveste
            renten. Likevel kan du fort velge feil lån dersom du ikke kjenner til hva de ulike
            rentebegrepene innebærer.
            <br />
            <br />
            Før vi forteller deg hvorfor, må du vite hva som danner utgangspunktet for renten du får
            på lånet ditt.
          </p>
          <Button
            color="yellow"
            className={debtCheckButtonClassName}
            onClick={handleDebtCheckClick}
          >
            Få lavere rente på forbrukslån og kredittkort
          </Button>
        </div>
      </Section>
      <Section background="paper" last>
        <div className={articleRowClassName}>
          <div className="w-full sm:w-1/2 sm:pr-6">
            <h3 className="mb-4 typography-v1-legacy-subtitle1Bold">Hva er renter?</h3>
            <p className="mb-8 typography-v1-legacy-p3 sm:mb-14">
              Som du sikkert har hørt mange ganger før er det ingenting i livet som er gratis, og
              det samme gjelder når du skal ta opp lån. Selve definisjonen på renter er betalingen
              eller kompensasjonen banken tar når du låner penger av dem.
              <br />
              <br />
              Du vet at boliglån og for så vidt billån har lav rente, mens{' '}
              <button
                type="button"
                className={chatTriggerButtonClassName}
                onClick={handleDebtCheckClick}
              >
                forbrukslån
              </button>{' '}
              og kredittkort har høyere rente. Årsaken er fordi at på lån til bolig og bil stiller
              du en sikkerhet som har verdi for banken, mens du på forbrukslån og kredittkort ikke
              trenger å stille sikkerhet. Renten blir derfor høyere, fordi banken tar en høyere
              risiko for å låne deg penger.
              <br />
              <br />
              Prosenttallet på renten forteller deg hvor mye ekstra du må betale, og når du tar opp
              et lån får du alltid vite nominell og effektiv rente. Men det er kun én av disse to
              som viser hva lånet koster deg.
            </p>

            <h3 className="mb-4 typography-v1-legacy-subtitle1Bold">Hva er nominell rente?</h3>
            <p className="mb-8 typography-v1-legacy-p3 sm:mb-14">
              Nominell rente er prisen banken setter for å låne deg penger. Dette er grunnrenten på
              lånet ditt, og er uten gebyrer, kostnader og andre avgifter. Den nominelle renten
              viser deg derfor ikke hva du skal betale på lånet ditt.
            </p>
          </div>

          <div className="w-full sm:w-1/2 sm:pl-6">
            <h3 className="mb-4 typography-v1-legacy-subtitle1Bold">Hva er effektiv rente?</h3>
            <p className="mb-8 typography-v1-legacy-p3 sm:mb-14">
              Effektiv rente er den du betaler totalt for lånet ditt. Det betyr at den er inkludert
              nominell rente, gebyrer, kostnader og andre avgifter. Den effektive renten viser deg
              dermed hva lånet ditt faktisk koster deg.
              <br />
              <br />
              Hvor mye vil lånet ditt koste? Sjekk forbrukslånskalkulatoren vår!
            </p>

            <h3 className="mb-4 typography-v1-legacy-subtitle1Bold">
              Hva er forskjellen på nominell og effektiv rente
            </h3>
            <p className="mb-8 typography-v1-legacy-p3 sm:mb-14">
              I første rekke er nominell rente alltid lavere enn den effektive – som altså viser den
              totale lånekostnaden. Forskjellen på å velge feil fordi du gikk for laveste nominelle
              rente og ikke laveste effektive, kan derfor koste deg mange tusen kroner i året. Så
              ikke la deg friste av lav nominell rente – når du skal sammenligne betingelser på
              lånet ditt, er det alltid den effektive renten du må se på.
              <br />
              <br />
              Bare se på tabellen under hvor mye dette kan utgjøre per måned og totalt. Det som kan
              virke som like betingelser, kan etter hvert bli til store summer.
            </p>
          </div>

          <InterestComparisonTable />

          <div className="w-full sm:w-1/2 sm:pr-6">
            <p className="mb-8 typography-v1-legacy-p3 sm:mb-14">
              En god tommelfingerregel når det gjelder lån er at jo raskere du betaler tilbake
              pengene, desto lavere blir totalkostnaden.
            </p>

            <h3 className="mb-4 typography-v1-legacy-subtitle1Bold">
              Hvordan får jeg best rente på lånet mitt?
            </h3>
            <p className="mb-8 typography-v1-legacy-p3 sm:mb-14">
              Når du søker om lån, foretar bankene alltid en individuell kredittsjekk. Her veier de
              flere ting opp mot hverandre, som blant annet inntekten, formuen og gjelden din, og
              kredittsjekken viser hvor høy lånebelastning økonomien din tåler.
              <br />
              <br />
              På bakgrunn av dette får du en kredittscore fra 0 til 100 som viser deg hvor
              betalingsdyktig du er, og det er denne som avgjør hvilken rente du får og hvor mye du
              får låne – dersom du i det hele tatt blir tilbudt lån. Du skal jo betale tilbake også.
            </p>
          </div>

          <div className="w-full sm:w-1/2 sm:pl-6">
            <p className="mb-8 typography-v1-legacy-p3 sm:mb-14">
              Tallet på kredittscoren er basert på allerede nevnte risiko som bankene veier
              forskjellig, og derfor får du også ulike renter. Så hvordan finner du lånet med de
              beste betingelsene for deg, og som gjør at du sparer tid og penger?
              <br />
              <br />
              Jo, ved å bruke sammenligningstjenesten Scoopr, som gjør hele prosessen mye enklere
              for deg. Når du sender lånesøknaden din til oss, går den automatisk videre til 13
              banker som konkurrerer om å gi deg de beste betingelsene. Du kan låne fra 10.000 til
              600.000 kroner, og rentesatsene ligger fra omtrent 4,9 til over 20 prosent.
              <br />
              <br />
              <strong>
                Det er gratis og uforpliktende å søke om forbrukslån og{' '}
                <RouterLink
                  className={`${linkClassName} font-bold`}
                  to={routes.PRODUCTS_REFINANCING}
                >
                  refinansiering
                </RouterLink>{' '}
                hos Scoopr, og når du har mottatt tilbudene fra bankene kan du selv vurdere hvilket
                som passer best for deg. Og husk – se etter det lånet som gir deg lavest effektiv
                rente.
              </strong>
            </p>
          </div>
        </div>

        <div className="mt-10 flex flex-col items-center">
          <Button
            color="yellow"
            className={debtCheckButtonClassName}
            onClick={handleDebtCheckClick}
          >
            Få lavere rente på forbrukslån og kredittkort
          </Button>
          <Disclaimer />
        </div>
      </Section>
    </Page>
  );
};

export default InterestRate;
